/**
 * Icons for help collection/category cards.
 *
 * Primary: a curated, subject-appropriate Material icon per known collection ({@link CATEGORY_ICONS}), matched
 * on the normalized (trimmed, lower-cased) name. Fallback: a deterministic hash of the name over a small
 * generic pool, so a collection added in Notion that we haven't mapped yet still gets a stable (if arbitrary)
 * icon. When a new collection ships, add a row to CATEGORY_ICONS so its card gets a fitting icon.
 *
 * Names are classic Material Icons ligatures (the `Material Icons` font is loaded app-wide).
 */

/** Subject-appropriate icon per known help collection, keyed by the lower-cased collection name. */
const CATEGORY_ICONS: Record<string, string> = {
  'getting started': 'rocket_launch',
  'account & settings': 'manage_accounts',
  security: 'security',
  announcements: 'campaign',
  'billing & payments': 'credit_card',
  'invoice creation & management': 'receipt_long',
  cases: 'folder_open',
  contacts: 'contacts',
  'client communication': 'forum',
  'client import & management': 'group_add',
  'daily docket': 'event',
  'document center': 'description',
  'eviction case management': 'gavel',
  'eviction processing': 'sync',
  'evictions management': 'balance',
  'property management': 'home_work',
  'task & workflow management': 'account_tree',
};

/** Generic pool for collections not in {@link CATEGORY_ICONS} — chosen deterministically from the name. */
const FALLBACK_ICONS = [
  'help_outline',
  'description',
  'folder_open',
  'insights',
  'people',
  'settings',
  'mail',
  'receipt_long',
];

/**
 * Material icon for a collection/category name: the curated icon when we have one, otherwise a deterministic
 * hash-based fallback. The same name always yields the same icon.
 */
export function helpIcon(name: string): string {
  const key = name.trim().toLowerCase();
  const mapped = CATEGORY_ICONS[key];
  if (mapped) {
    return mapped;
  }
  // Hash the SAME normalized key used for the lookup, so capitalization/whitespace variants of one collection
  // ("Billing" / "billing" / " Billing ") always fall back to the same icon.
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_ICONS[hash % FALLBACK_ICONS.length];
}

/** Bucket key for an article's top-level collection — its Section, or "General" when unset. */
export const GENERAL_COLLECTION = 'General';
